import dropDownLocators from '@app/e2e/locators/components/dropDown';
import {addTestId} from '@app/helpers/addTestId';
import {ClassNameProps, FloatingUIElementProps} from '@app/helpers/react';
import Luiv3Constants from '@app/lui3/constants';
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  FloatingTree,
  offset,
  OffsetOptions,
  Placement,
  shift,
  size,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
} from '@floating-ui/react';
import {
  createContext,
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {mergeRefs} from 'react-merge-refs';
import {Transition, TransitionGroup} from 'react-transition-group';
import styled from 'styled-components';

import FloatingList from '../FloatingList';
import FloatingPortal from '../FloatingPortal';
import {LabelResetProvider} from '../Label';
import useMediaQuery from '../utils/useMediaQuery';
import autoPlacement from './autoPlacement';
import transformOrigin from './transformOrigin';
import {useConditionalClick} from './useConditionalClick';

interface Props extends ClassNameProps {
  anchor: ReactNode | ((props: FloatingUIElementProps) => ReactNode);
  children?: ((close: () => void) => ReactNode) | ReactNode | undefined;
  fitToAnchor?: boolean;
  offset?: boolean;
  offsetX?: boolean;
  persistent?: boolean;
  minWidth?: boolean;
  alwaysOpenDown?: boolean;
  placement?: Placement;
  offsetOptions?: OffsetOptions;
  radius?: Radius;
  onClose?: () => void;
  removeContainerPadding?: boolean;
  fullWidthAnchor?: boolean;
  onOpenChange?: (open: boolean) => void;
  nestInParent?: boolean;
  disableFocus?: boolean;
  applyBorder?: boolean;
  fullSize?: boolean;
  modal?: boolean;
}

type Radius = 'square' | 'small' | 'normal';

export const AnchorContainer = styled.div<{fullWidth?: boolean}>`
  display: inline-grid;
  ${({fullWidth}) =>
    fullWidth &&
    `
    display: grid;
    width: 100%;
  `}
`;

const DropdownContainer = styled(FloatingList)<{
  removeContainerPadding?: boolean;
  applyBorder?: boolean;
}>`
  min-width: 25ch;
  max-width: 50ch;
  ${({removeContainerPadding}) => removeContainerPadding && `padding: 0;`}
  ${({applyBorder}) =>
    applyBorder && `border: 1px solid ${Luiv3Constants.baseColor['gray-200']};`}
  &[data-min-width='false'] {
    min-width: 0;
  }
`;

const InternalContainer = styled.div`
  display: contents;
  :is(:not(:empty) + &) {
    display: none;
  }
  :not(:last-of-type) {
    display: none;
  }
`;

const DropdownNodeContext = createContext<Element | null>(null);

const DropdownComponent = forwardRef<HTMLElement, Props>(
  (
    {
      anchor,
      children,
      className,
      fitToAnchor = false,
      offset: doOffset = false,
      offsetX = false,
      persistent = false,
      minWidth = true,
      alwaysOpenDown = false,
      placement,
      offsetOptions,
      radius = 'normal',
      onClose,
      removeContainerPadding,
      fullWidthAnchor = false,
      onOpenChange,
      nestInParent = false,
      disableFocus = false,
      applyBorder = false,
      fullSize = false,
      modal = false,
    },
    ref
  ) => {
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (!open && onClose) {
        onClose();
      }
    }, [open]);

    useEffect(() => {
      onOpenChangeRef.current?.(open);
    }, [open]);

    const nodeId = useFloatingNodeId();
    const parentId = useFloatingParentNodeId();
    const parentDropdownEl = useContext(DropdownNodeContext);
    const resolvedParentDropdownEl = nestInParent ? null : parentDropdownEl;
    const nested = parentId != null;

    const offsetMiddleware = offset(
      offsetOptions ??
        (({rects}) =>
          doOffset || offsetX || nested ? 0 : -rects.reference.height)
    );

    const commonMiddleware = [
      size({
        apply({availableWidth, availableHeight}) {
          if (refs.floating.current) {
            if (!fullSize) {
              Object.assign(refs.floating.current.style, {
                maxWidth: `${availableWidth}px`,
                maxHeight: `${availableHeight}px`,
              });
            }
            if (fitToAnchor && refs.domReference.current) {
              Object.assign(refs.floating.current.style, {
                width: `${refs.domReference.current.offsetWidth}px`,
              });
            }
          }
        },
        padding: 16,
      }),
      transformOrigin({
        apply({x, y}) {
          if (refs.floating.current) {
            Object.assign(refs.floating.current?.style, {
              transformOrigin: `${x}px ${y}px`,
            });
          }
        },
      }),
    ];

    const middleware = () => {
      const optionalMiddleware = [];
      if (!alwaysOpenDown && !placement) {
        optionalMiddleware.push(autoPlacement(nested || offsetX));
      }
      if (fullSize) {
        optionalMiddleware.push(
          shift({mainAxis: true, crossAxis: true, padding: 8})
        );
      }
      return [offsetMiddleware, ...optionalMiddleware, ...commonMiddleware];
    };

    const {x, y, strategy, refs, context} = useFloating<HTMLElement>({
      open,
      nodeId,
      onOpenChange: setOpen,
      whileElementsMounted: autoUpdate,
      strategy: 'fixed',
      placement,
      middleware: middleware(),
    });

    const {getReferenceProps, getFloatingProps} = useInteractions([
      useConditionalClick(context),
      useDismiss(context),
    ]);

    const mergedReferenceRef = useMemo(
      () => mergeRefs([ref, refs.setReference]),
      [ref]
    );

    const disableAnimations = useMediaQuery('(prefers-reduced-motion: reduce)');

    const props = getReferenceProps({
      ref: mergedReferenceRef,
      'aria-expanded': open,
    });

    const close = useCallback(() => {
      requestAnimationFrame(() => setOpen(false));
    }, []);

    const dropdownEl = useRef<HTMLDivElement>(null);

    const renderedChildren = (
      <LabelResetProvider>
        {typeof children === 'function' ? children(close) : children}
      </LabelResetProvider>
    );

    return (
      <FloatingNode id={nodeId}>
        {typeof anchor === 'function' ? (
          anchor(props)
        ) : (
          <AnchorContainer {...props} fullWidth={fullWidthAnchor}>
            {anchor}
          </AnchorContainer>
        )}
        {resolvedParentDropdownEl == null ? (
          <FloatingPortal useParentPriority>
            <TransitionGroup component={null}>
              {open && (
                <Transition
                  enter={!disableAnimations}
                  exit={false}
                  nodeRef={refs.floating}
                  addEndListener={(done) => {
                    refs.floating.current?.addEventListener(
                      'transitionend',
                      done,
                      false
                    );
                    refs.floating.current?.addEventListener(
                      'animationend',
                      done,
                      false
                    );
                  }}
                >
                  {(state) => (
                    <FloatingFocusManager
                      context={context}
                      disabled={disableFocus}
                    >
                      <>
                        {modal && <FloatingOverlay />}
                        <DropdownContainer
                          data-min-width={minWidth}
                          data-radius={radius}
                          {...getFloatingProps({
                            ref: refs.setFloating,
                            style: {
                              position: strategy,
                              top: y ?? '',
                              left: x ?? '',
                            },
                          })}
                          className={`${className || ''} ${state}`}
                          removeContainerPadding={removeContainerPadding}
                          applyBorder={applyBorder}
                        >
                          <div style={{display: 'contents'}} ref={dropdownEl} />
                          <InternalContainer
                            onClick={(!persistent && close) || undefined}
                            data-persistent={persistent}
                            {...addTestId(dropDownLocators.list)}
                          >
                            <DropdownNodeContext.Provider
                              value={dropdownEl.current}
                            >
                              {renderedChildren}
                            </DropdownNodeContext.Provider>
                          </InternalContainer>
                        </DropdownContainer>
                      </>
                    </FloatingFocusManager>
                  )}
                </Transition>
              )}
            </TransitionGroup>
          </FloatingPortal>
        ) : (
          open &&
          createPortal(
            <FloatingFocusManager context={context} disabled={disableFocus}>
              <InternalContainer
                {...getFloatingProps({
                  ref: refs.setFloating,
                })}
                className={className}
                data-persistent={persistent}
                onClick={(!persistent && close) || ((e) => e.stopPropagation())}
                {...addTestId(dropDownLocators.list)}
              >
                {renderedChildren}
              </InternalContainer>
            </FloatingFocusManager>,
            resolvedParentDropdownEl
          )
        )}
      </FloatingNode>
    );
  }
);

const Dropdown = forwardRef<HTMLElement, Props>((props, ref) => {
  const parentId = useFloatingParentNodeId();

  if (parentId == null) {
    return (
      <FloatingTree>
        <DropdownComponent {...props} ref={ref} />
      </FloatingTree>
    );
  }

  return <DropdownComponent {...props} ref={ref} />;
});

export default Dropdown;
